<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  groupParticipantName,
  messageSourceIcon,
  messageSourceLabel,
} = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]:
    'bg-n-blue-10 text-white border border-n-blue-9 shadow-md shadow-n-blue-12/10',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]:
    'bg-n-solid-3 text-n-slate-12 border border-n-weak shadow-sm',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]:
    'bg-n-iris-4 text-n-iris-12 border border-n-iris-7 shadow-sm',
  [MESSAGE_VARIANTS.EXTERNAL]:
    'bg-n-teal-4 text-n-teal-12 border border-n-teal-7 shadow-sm',
  [MESSAGE_VARIANTS.TEMPLATE]:
    'bg-n-iris-4 text-n-iris-12 border border-n-iris-7 shadow-sm',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-2xl ltr:rounded-bl-md rtl:rounded-br-md',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-2xl ltr:rounded-br-md rtl:rounded-bl-md',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const shouldShowGroupParticipantName = computed(() => {
  return groupParticipantName?.value && !shouldGroupWithNext.value;
});

const shouldShowSourceLabel = computed(() => {
  if (!messageSourceLabel?.value) return false;
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) return false;
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return false;

  return !shouldGroupWithNext.value;
});

const sourceLabelClass = computed(() => {
  const map = {
    [MESSAGE_VARIANTS.AGENT]:
      'bg-white/15 text-white border-white/20 dark:bg-white/15 dark:text-white',
    [MESSAGE_VARIANTS.EXTERNAL]:
      'bg-n-teal-9/10 text-n-teal-11 border-n-teal-8/40',
    [MESSAGE_VARIANTS.BOT]: 'bg-n-iris-9/10 text-n-iris-11 border-n-iris-8/40',
  };

  return map[variant.value] || 'bg-n-alpha-2 text-n-slate-11 border-n-weak';
});

const metaClass = computed(() => {
  const map = {
    [MESSAGE_VARIANTS.AGENT]: 'text-white/70',
    [MESSAGE_VARIANTS.EXTERNAL]: 'text-n-teal-11/70',
    [MESSAGE_VARIANTS.BOT]: 'text-n-iris-11/70',
    [MESSAGE_VARIANTS.PRIVATE]: 'text-n-amber-12/50',
  };

  return map[variant.value] || 'text-n-slate-11';
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-[calc(100vw-5rem)] sm:max-w-xl xl:max-w-2xl':
          variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <div v-if="shouldShowGroupParticipantName" class="min-w-0 mb-1">
      <span
        class="min-w-0 text-sm font-semibold leading-5 truncate text-n-blue-11"
      >
        {{ groupParticipantName }}
      </span>
    </div>
    <div v-if="shouldShowSourceLabel" class="mb-2">
      <span
        class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-4"
        :class="sourceLabelClass"
      >
        <span v-if="messageSourceIcon" :class="messageSourceIcon" />
        {{ messageSourceLabel }}
      </span>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        metaClass,
      ]"
      class="mt-2"
    />
  </div>
</template>
